import React, { FC, useEffect, useState } from 'react';
import * as propz from 'propz';
import { Tournament } from 'models/tournament';
import { School } from 'models/school';
import { Record as RecordModel } from 'models/record';
import { useTournamentTVData } from 'hooks/useTournamentTVData';
import { getSchoolById } from 'services/school';
import { getNationalAthleticScreensData } from 'utils/tournamentTVUtils';
import { getRecords } from 'services/getRecords';
import {
  isIndividualTournament,
  isRegularIndividualTournamentSubtype,
  isAthleticSwimmingTournamentSubtype,
} from 'helpers/tournament';
import GroupScreen from 'components/TV/TVGroupScreen';
import TVFooter from 'components/TV/TVFooter';
import PageNotFound from 'views/School/PageNotFound';
import './TournamentTV.scss';
import TeamTournamentBigScreen from './TeamTournamentBigScreen';
import IndividualTournamentBigScreen from './IndividualTournamentBigScreen';

interface TournamentTVProps {
  tournament: Tournament;
}

const LegacyTournamentTV: FC<TournamentTVProps> = ({ tournament }) => {
  const bigScreenStatus = propz.get(
    tournament,
    ['publicBigscreenSite', 'status'],
    'DISABLED'
  );

  const { events, isSync: isBaseDataSync, error } = useTournamentTVData(tournament);

  const [unionSchool, setUnionSchool] = useState<School | null>(null);
  const [isSchoolLoading, setIsSchoolLoading] = useState(true);
  useEffect(() => {
    const schoolId = propz.get(tournament, ['schoolId'], '') as string;
    if (!schoolId) {
      setUnionSchool(null);
      setIsSchoolLoading(false);
      return;
    }
    setIsSchoolLoading(true);
    getSchoolById(schoolId)
      .then((school: School) => {
        setUnionSchool(school);
        setIsSchoolLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load union school:', err);
        setUnionSchool(null);
        setIsSchoolLoading(false);
      });
  }, [tournament]);

  const [allRecords, setAllRecords] = useState<RecordModel[]>([]);
  const [isRecordsLoading, setIsRecordsLoading] = useState(true);
  useEffect(() => {
    const schoolId = propz.get(tournament, ['schoolId'], '') as string;
    if (!schoolId) {
      setAllRecords([]);
      setIsRecordsLoading(false);
      return;
    }
    setIsRecordsLoading(true);
    getRecords(schoolId)
      .then((records: RecordModel[]) => {
        console.log('loaded allRecords:', records);
        setAllRecords(records);
        setIsRecordsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load records:', err);
        setAllRecords([]);
        setIsRecordsLoading(false);
      });
  }, [tournament]);

  const [screenData, setScreenData] = useState<any[]>([]);
  const [isScreensReady, setIsScreensReady] = useState(false);
  useEffect(() => {
    if (!isBaseDataSync) return;

    if (isIndividualTournament(tournament)) {
      if (
        isRegularIndividualTournamentSubtype(tournament) ||
        isAthleticSwimmingTournamentSubtype(tournament)
      ) {
        setScreenData(getNationalAthleticScreensData(events));
      } else {
        setScreenData([]);
      }
    } else {
      setScreenData([]);
    }
    setIsScreensReady(true);
  }, [isBaseDataSync, events, tournament]);

  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    if (currentIndex >= screenData.length) setCurrentIndex(0);
  }, [currentIndex, screenData.length]);

  const autoScrollingDelay =
  Math.max(3, Number(propz.get(tournament, ['publicBigscreenSite', 'autoScrollingDelay'], 6)) || 6);

  useEffect(() => {
    if (!isScreensReady || screenData.length === 0 || tournament.publicBigscreenSite?.['autoplay'] === false) return;

    const slide = screenData[currentIndex];

    const slideDelayMs = autoScrollingDelay * 1000;

    const timeoutId = window.setTimeout(() => {
      setCurrentIndex((idx) =>
        idx < screenData.length - 1 ? idx + 1 : 0
      );
    }, slideDelayMs);

    return () => clearTimeout(timeoutId);
  }, [
    isScreensReady,
    screenData,
    autoScrollingDelay,   
    currentIndex,
    tournament.publicBigscreenSite,
  ]);



  if (bigScreenStatus === 'DISABLED') {
    return <PageNotFound />;
  }

  if (isSchoolLoading || !isBaseDataSync || !isScreensReady || isRecordsLoading) {
    return (
      <div style={{ color: 'white', textAlign: 'center', marginTop: '20vh' }}>
        Loading…
      </div>
    );
  }

  const slide = screenData[currentIndex] || screenData[0];
  const picUrl = propz.get(
    tournament,
    ['photos', 0, 'picUrl'],
    '/images/default_bigscreen_background.jpg'
  );
  const wrapperStyle = { backgroundImage: `url(${picUrl})` };

  const showFooter = false;

  return (
    <div className="bBigScreenAmbience" style={wrapperStyle}>
      {slide && (
        <GroupScreen
          data={slide}
          tournament={tournament}
          unionSchool={unionSchool || undefined}
          allRecords={allRecords}
        />
      )}

      {!slide && <div style={{ color: 'white', textAlign: 'center', marginTop: '20vh' }}>{error || 'Tournament events will appear here automatically.'}</div>}
      {slide && error && <div style={{ position: 'absolute', bottom: 8, right: 16, color: 'white', background: 'rgba(0,0,0,0.7)', padding: 8 }}>{error}</div>}

      {showFooter && slide && (
        <TVFooter
          events={slide.events}
          activeSchoolId={tournament.schoolId as string}
        />
      )}
    </div>
  );
};

const TournamentTV: FC<TournamentTVProps> = ({tournament}) => tournament.type === 'INDIVIDUAL_TOURNAMENT' && (!tournament.publicBigscreenSite || tournament.publicBigscreenSite.status === 'DISABLED')
  ? <PageNotFound />
  : tournament.type === 'TEAM_TOURNAMENT'
  ? <TeamTournamentBigScreen tournament={tournament}/>
  : tournament.publicSite?.templateVersion === 'INDIVIDUAL_TOURNAMENT_V2'
    ? <IndividualTournamentBigScreen tournament={tournament}/>
    : <LegacyTournamentTV tournament={tournament}/>;

export default TournamentTV;
